import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { useSocialLinksQuery } from '@modules/ThemeEditorV2/modules/SocialLinks'

import { useKudosOrdersCountQuery } from '../../../Home/queries/useKudosOrdersCountQuery'
import { useCurrentShopQuery } from '../../../hooks/useCurrentShopQuery'
import { useUnfulfilledOrdersCountQuery } from '../../../hooks/useUnfulfilledOrdersCountQuery'
import { useCreatorIntegrationsQuery } from '../../../pages/Apps'
import type { Promotion } from '../../../pages/Promotions/Promotions.types'
import { usePromotionsQuery } from '../../../pages/Promotions/pages/PromotionCodes'
import { useCurrentDomain } from '../../../pages/Settings/pages/Domain/modules/ClaimDomain'
import { useCartAbandonmentEmailPromoRecommendation } from '../../Recommendations/modules/CartAbandonmentEmailPromoRecommendation'

/**
 * Hook providing data for mini recommendations.
 *
 * Includes information about:
 * - orders
 * - thank you acknowledgments
 * - cart abandonment email promotions
 *
 * Note: Data must be fetched here (not inside slides)
 * because Slider component needs to know the number of slides in advance.
 */

export const useMiniRecommendationsData = () => {
  const { hasPermission } = useDashboardPermissions()

  // Free shipping
  const { promotionsQuery } = usePromotionsQuery()
  const promotions = promotionsQuery.data?.promotions as Promotion[]

  // OrdersToSend
  const { unfulfilledOrderCountQuery } = useUnfulfilledOrdersCountQuery(undefined, {
    enabled: hasPermission('contributions.fulfillments.view'),
  })

  // Thank Yous:
  const { kudosOrdersCountQuery } = useKudosOrdersCountQuery([{ state: ['WAITING'] }], {
    enabled: hasPermission('contributions.thankYous'),
  })

  // AbandonmentEmails:
  const { isLoadingCartAbandonmentEmailPromoRecommendation } =
    useCartAbandonmentEmailPromoRecommendation()

  // Twitch and YouTube integrations
  const { currentShopQuery } = useCurrentShopQuery()
  const { socialLinksQuery } = useSocialLinksQuery([currentShopQuery.data?.id!], {
    enabled: currentShopQuery.isSuccess && !!currentShopQuery.data?.id,
    staleTime: 60_000,
  })

  const appsEnabled = hasPermission('apps')
  const { creatorIntegrationsQuery } = useCreatorIntegrationsQuery(undefined, {
    enabled: appsEnabled,
    staleTime: 60_000,
  })
  const isAppsEnabledLoading = appsEnabled && creatorIntegrationsQuery.isLoading

  const { canClaimDomain, isLoading: isLoadingCurrentDomain } = useCurrentDomain()

  return {
    isLoading:
      promotionsQuery.isLoading ||
      unfulfilledOrderCountQuery.isInitialLoading ||
      kudosOrdersCountQuery.isInitialLoading ||
      isLoadingCartAbandonmentEmailPromoRecommendation ||
      currentShopQuery.isLoading ||
      socialLinksQuery.isLoading ||
      isLoadingCurrentDomain ||
      isAppsEnabledLoading,
    data: {
      freeShipping: {
        promotions,
      },
      ordersToSend: unfulfilledOrderCountQuery.data,
      thankYous: {
        kudosOrdersCount: kudosOrdersCountQuery.data,
      },
      twitchGifting: {
        socialLinks: socialLinksQuery.data,
        creatorIntegrations: creatorIntegrationsQuery.data,
      },
      youtubeShelf: {
        socialLinks: socialLinksQuery.data,
        creatorIntegrations: creatorIntegrationsQuery.data,
      },
      claimFreeDomain: {
        canClaimDomain,
      },
    },
  }
}
